import React, {useEffect, useRef, useState, useCallback} from 'react';
import { TooltipProvider, Tooltip as UITooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { CircleHelp } from 'lucide-react';
import { useTranslation } from "react-i18next";
import axiosInstance from '@/api/axios.js';
import * as d3 from 'd3';

const ComplianceCostSavingsCard = ({selectedBins, selectedPeriod}) => {
    const svgRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const { t } = useTranslation();

    // Detect dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            const isDark = document.documentElement.classList.contains('dark');
            setIsDarkMode(isDark);
        };
        
        checkDarkMode();
        
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { 
            attributes: true, 
            attributeFilter: ['class'] 
        });
        
        return () => observer.disconnect();
    }, []);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const params = {
                    timeRange: selectedPeriod || 'Year',
                    bins: selectedBins.join(',')
                };

                const response = await axiosInstance.get('cost_savings_page', {params});
                setChartData(response.data.compliance_savings);
            } catch (error) {
                console.error("Error fetching compliance data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (selectedBins && selectedBins.length > 0) {
            fetchData();
        }
    }, [selectedBins, selectedPeriod]);

    // Create tooltip
    const createTooltip = useCallback(() => {
        return d3.select('body')
            .append('div')
            .attr('class', 'compliance-tooltip')
            .style('position', 'absolute')
            .style('background', isDarkMode ? '#1f2937' : 'white')
            .style('color', isDarkMode ? 'white' : 'black')
            .style('padding', '8px 12px')
            .style('border-radius', '6px')
            .style('font-size', '14px')
            .style('box-shadow', '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)')
            .style('border', `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`)
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('z-index', 1000);
    }, [isDarkMode]);

    // Draw chart with D3
    const drawChart = useCallback(() => {
        if (!svgRef.current || !chartData) return;

        // Clear previous chart
        d3.select(svgRef.current).selectAll("*").remove();

        const savedAmount = chartData.cost_savings || chartData.current_year || 0;
        const total = savedAmount / (chartData.compliance_percentage / 100) || 1;
        const remainingAmount = Math.max(0, total - savedAmount);

        // Chart dimensions
        const width = svgRef.current.clientWidth;
        const height = svgRef.current.clientHeight;
        const radius = Math.min(width, height) / 2;
        const innerRadius = radius * 0.55; // 55% inner radius for donut

        // Colors
        const savedColor = '#F27405';
        const remainingColor = isDarkMode ? '#374151' : '#F2F2F2';
        const savedHoverColor = '#E06C00';
        const remainingHoverColor = isDarkMode ? '#4B5563' : '#E0E0E0';

        // Create SVG
        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${height / 2})`);

        // Create pie generator
        const pie = d3.pie()
            .value(d => d.value)
            .sort(null);

        // Create arc generator
        const arc = d3.arc()
            .innerRadius(innerRadius)
            .outerRadius(radius);

        // Data
        const data = [
            {
                label: t('complianceSavings.penaltiesAvoided'),
                value: savedAmount,
                color: savedColor,
                hoverColor: savedHoverColor
            },
            {
                label: t('complianceSavings.potentialPenalties'),
                value: remainingAmount,
                color: remainingColor,
                hoverColor: remainingHoverColor
            }
        ];

        // Create tooltip
        const tooltip = createTooltip();

        // Draw arcs
        const arcs = svg.selectAll('.arc')
            .data(pie(data))
            .enter()
            .append('g')
            .attr('class', 'arc');

        const path = arcs.append('path')
            .attr('d', arc)
            .attr('fill', d => d.data.color)
            .attr('stroke', isDarkMode ? '#1f2937' : 'white')
            .attr('stroke-width', 2)
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d) {
                // Highlight slice
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('fill', d.data.hoverColor);

                // Show tooltip
                const percent = (d.data.value / total * 100).toFixed(1);
                let tooltipText;
                if (d.data.label === t('complianceSavings.penaltiesAvoided')) {
                    tooltipText = `${t('complianceSavings.saved')} ${percent}% (€${d.data.value.toLocaleString()})`;
                } else {
                    tooltipText = `${t('complianceSavings.potential')} ${percent}% (€${d.data.value.toLocaleString()})`;
                }

                tooltip
                    .style('opacity', 1)
                    .html(`
                        <div class="font-semibold">${d.data.label}</div>
                        <div>${tooltipText}</div>
                    `);
            })
            .on('mousemove', function(event) {
                tooltip
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY - 10) + 'px');
            })
            .on('mouseout', function(event, d) {
                // Restore original color
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('fill', d.data.color);

                // Hide tooltip
                tooltip.style('opacity', 0);
            });

        // Add click interaction (optional)
        path.on('click', function(event, d) {
            console.log('Clicked:', d.data);
        });

        // Cleanup function
        return () => {
            tooltip.remove();
        };
    }, [chartData, isDarkMode, t, createTooltip]);

    // Draw chart when data or dimensions change
    useEffect(() => {
        if (chartData && !isLoading) {
            const cleanup = drawChart();

            // Handle resize
            const handleResize = () => {
                drawChart();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                if (cleanup) cleanup();
            };
        }
    }, [chartData, isLoading, drawChart]);

    const savedPercentage = chartData
        ? Math.round(chartData.compliance_percentage || chartData.compliance_rate || 0)
        : 0;

    return (
        <div className="sm:rounded-lg bg-muted/50 p-4 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                    {t('complianceSavings.title')}
                </h4>
                <TooltipProvider>
                    <UITooltip>
                        <TooltipTrigger asChild>
                            <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs">
                                The compliance savings card shows how much money you're saving by recycling properly instead of throwing everything in the trash.<br/>
                                When you sort waste into recyclable categories like plastic (PMD), organic, and paper, you avoid expensive landfill fees.
                            </p>
                        </TooltipContent>
                    </UITooltip>
                </TooltipProvider>
            </div>
            <div className="flex-grow relative">
                {isLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                        {t('complianceSavings.loading')}
                    </div>
                ) : (
                    <>
                        <svg
                            ref={svgRef}
                            className="w-full h-full"
                            style={{ minHeight: '200px' }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                                <p className="text-2xl lg:text-3xl font-bold text-foreground">
                                    {savedPercentage}%
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ComplianceCostSavingsCard;